import { cn } from '@today/ui-primitives'
import type { ChangeEvent, DragEvent, ReactElement } from 'react'
import { useId } from 'react'

/**
 * Props for the {@link Upload} molecule.
 *
 * @property label - Visible prompt on the drop zone and the file input's accessible name.
 * @property files - The consumer's current selection, rendered by name.
 * @property onFilesSelected - Reports chosen or dropped files. Transmission is the consumer's job.
 * @property accept - Forwarded to the file input.
 * @property multiple - Forwarded to the file input; when false at most one file is reported.
 * @property hint - Optional supporting text under the prompt.
 * @property className - Extra Tailwind classes merged onto the drop zone.
 *
 * @example
 * ```tsx
 * const { files, select } = useAttachmentUpload()
 *
 * <Upload label="Attachments" accept=".pdf" multiple files={files} onFilesSelected={select} />
 * ```
 */
export interface UploadProps {
	label: string
	files: File[]
	onFilesSelected: (files: File[]) => void
	accept?: string
	multiple?: boolean
	hint?: string
	className?: string
}

/**
 * File picker with a drop zone — it reports the chosen files and nothing else.
 *
 * @remarks
 * - **It never transmits anything.** No request is issued here; the consumer's
 *   hook owns the upload, its progress, its retries and its errors. That is the
 *   ADR-0031 seam: I/O does not belong in a view.
 * - **Two input paths, one contract:** choosing through the file input and
 *   dropping on the zone both report a `File[]` through `onFilesSelected`. When
 *   `multiple` is false at most one file is reported, however many arrive.
 * - **Controlled display:** the file names shown come from the `files` prop, so
 *   the consumer decides what counts as selected.
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/upload/upload.stories.tsx for visual examples
 */
/** The consumer's current selection, listed by name. */
function SelectedFiles({ files }: { files: File[] }): ReactElement | null {
	if (files.length === 0) return null

	return (
		<ul className="flex flex-col gap-1">
			{files.map((file) => (
				<li key={file.name} className="text-ds-sm text-ds-text-muted">
					{file.name}
				</li>
			))}
		</ul>
	)
}

export function Upload({
	label,
	files,
	onFilesSelected,
	accept,
	multiple,
	hint,
	className,
}: UploadProps): ReactElement {
	const inputId = useId()

	const report = (incoming: FileList | File[] | null) => {
		const list = Array.from(incoming ?? [])
		if (list.length === 0) return
		onFilesSelected(multiple ? list : list.slice(0, 1))
	}

	const onDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault()
		report(event.dataTransfer?.files ?? null)
	}

	return (
		<div className="flex flex-col gap-2">
			{/* biome-ignore lint/a11y/noStaticElementInteractions: the drop zone is a pointer-only convenience; the nested file input is the keyboard and screen-reader path. */}
			<div
				data-testid="upload-dropzone"
				onDrop={onDrop}
				onDragOver={(event) => event.preventDefault()}
				className={cn(
					'flex flex-col items-center gap-2 rounded-ds-lg border border-dashed border-ds-border bg-ds-surface p-6 text-center',
					className,
				)}
			>
				<label htmlFor={inputId} className="text-ds-base font-medium text-ds-text">
					{label}
				</label>
				{hint ? <p className="text-ds-sm text-ds-text-muted">{hint}</p> : null}
				<input
					id={inputId}
					data-testid="upload-input"
					type="file"
					accept={accept}
					multiple={multiple}
					onChange={(event: ChangeEvent<HTMLInputElement>) => report(event.target.files)}
					className="text-ds-sm text-ds-text-muted"
				/>
			</div>
			<SelectedFiles files={files} />
		</div>
	)
}
